// Reusable drawing board: pointer-driven canvas (mouse / touch / pen) plus toolbar.
// Strokes live in logical canvas units so the board can be any display size;
// games control width via container/className and shape via aspectRatio.
// View supports zoom/pan: one finger draws, two fingers pinch-zoom and pan
// (Procreate-style), ctrl+wheel / trackpad pinch zooms on desktop, plain wheel
// pans while zoomed in.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DRAWING_EXPORT_WIDTH } from '@airhead-games/shared/configs';
import { getColorStop } from '@/styles/colorStops';
import { Button } from '@/components/ui/button';
import { cn } from '@/utilities/cn';
import { LOGICAL_WIDTH } from './constants';
import { DrawingToolbar } from './DrawingToolbar';
import { CANVAS_BACKGROUND_COLOR, exportDrawing } from './exportDrawing';
import { renderStrokes } from './renderStrokes';
import { useDrawingState } from './useDrawingState';
import type { DrawingAction, Stroke, StrokePoint } from './types';
// Exports re-render the full drawing at a fixed resolution, independent of the
// current zoom/pan view and device pixel ratio
const EXPORT_WIDTH = DRAWING_EXPORT_WIDTH;

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

interface ViewState {
  zoom: number;
  panX: number; // logical units; top-left of the visible window
  panY: number;
}

const RESET_VIEW: ViewState = { zoom: 1, panX: 0, panY: 0 };

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

interface DrawingCanvasProps {
  /** width / height; games pick the shape (1, 4/3, 16/9, ...) */
  aspectRatio?: number;
  /** Receives the exported image; hides the Submit button when omitted */
  onSubmit?: (image: Blob) => void | Promise<void>;
  submitLabel?: string;
  className?: string;
  /**
   * The toolbar row mirrors the canvas frame's before/slot/after structure so
   * games can align both rows on one grid (give the two frames the same
   * grid-template-columns and the toolbar tracks the canvas column).
   */
  toolbarFrameClassName?: string;
  toolbarBefore?: React.ReactNode;
  toolbarAfter?: React.ReactNode;
  canvasFrameClassName?: string;
  canvasSlotClassName?: string;
  canvasClassName?: string;
  canvasBefore?: React.ReactNode;
  canvasAfter?: React.ReactNode;
  /**
   * Fires for every state-changing action as it happens. Wire this to the draw
   * relay (see useDrawArtistRelay) to stream the drawing live; omit it for a
   * purely local board.
   */
  onAction?: (action: DrawingAction) => void;
  /**
   * Server-accumulated canvas to restore after a refresh/rejoin (see
   * useDrawArtistSnapshot). Applied once, and only while the local canvas is
   * still untouched — if the artist has already drawn, their local state wins.
   */
  hydrateSnapshot?: { strokes: Stroke[]; currentStroke: Stroke | null } | null;
}

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  aspectRatio = 1,
  onSubmit,
  submitLabel = 'Submit',
  className,
  toolbarFrameClassName,
  toolbarBefore,
  toolbarAfter,
  canvasFrameClassName,
  canvasSlotClassName,
  canvasClassName,
  canvasBefore,
  canvasAfter,
  onAction,
  hydrateSnapshot,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{
    startDist: number;
    startMid: { x: number; y: number };
    startView: ViewState;
  } | null>(null);
  // After a pinch, a leftover finger must lift before drawing resumes
  const suppressDrawingRef = useRef(false);
  const [state, dispatch] = useDrawingState();
  const [view, setView] = useState<ViewState>(RESET_VIEW);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Current state can return to empty after Clear/Undo. Preserve the stronger
  // "artist has modified this canvas" fact so a late snapshot cannot restore
  // over that local decision. Tool-only changes do not consume hydration.
  const localDrawingTouchedRef = useRef(false);

  // Dispatch locally and relay the same action (when streaming). A ref keeps the
  // wrapper stable while always calling the latest onAction.
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;
  const applyAction = useCallback(
    (action: DrawingAction) => {
      switch (action.type) {
        case 'SET_COLOR':
        case 'SET_OPACITY':
        case 'SET_THICKNESS':
          break;
        default:
          localDrawingTouchedRef.current = true;
      }
      dispatch(action);
      onActionRef.current?.(action);
    },
    [dispatch]
  );

  // Restore the server-accumulated canvas exactly once, and only if the artist
  // hasn't drawn locally yet. Dispatched directly (not via applyAction): the
  // server already holds these strokes, so they must not re-relay.
  const hydratedRef = useRef(false);
  const hasLocalDrawing =
    state.strokes.length > 0 || state.currentStroke !== null;
  useEffect(() => {
    if (
      hydratedRef.current ||
      !hydrateSnapshot ||
      (hydrateSnapshot.strokes.length === 0 && !hydrateSnapshot.currentStroke)
    ) {
      return;
    }
    hydratedRef.current = true;
    if (localDrawingTouchedRef.current || hasLocalDrawing) {
      return;
    }
    dispatch({
      type: 'HYDRATE',
      strokes: hydrateSnapshot.strokes,
      currentStroke: hydrateSnapshot.currentStroke,
    });
  }, [hydrateSnapshot, dispatch, hasLocalDrawing]);

  const logicalHeight = LOGICAL_WIDTH / aspectRatio;

  const viewRef = useRef(view);
  viewRef.current = view;

  const clampView = useCallback(
    (next: ViewState): ViewState => {
      const zoom = clamp(next.zoom, MIN_ZOOM, MAX_ZOOM);
      return {
        zoom,
        panX: clamp(next.panX, 0, LOGICAL_WIDTH - LOGICAL_WIDTH / zoom),
        panY: clamp(next.panY, 0, logicalHeight - logicalHeight / zoom),
      };
    },
    [logicalHeight]
  );

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const scale = (canvas.width / LOGICAL_WIDTH) * view.zoom;
    ctx.setTransform(
      scale,
      0,
      0,
      scale,
      -view.panX * scale,
      -view.panY * scale
    );
    renderStrokes(
      ctx,
      state.currentStroke
        ? [...state.strokes, state.currentStroke]
        : state.strokes
    );
  }, [state.strokes, state.currentStroke, view]);

  // Keep the bitmap matched to display size * devicePixelRatio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      redraw();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [redraw]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  // Display position (CSS px) -> logical-viewport units, before zoom/pan
  const toViewportUnits = (clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * LOGICAL_WIDTH,
      y: ((clientY - rect.top) / rect.height) * logicalHeight,
    };
  };

  const toLogicalPoint = (e: React.PointerEvent): StrokePoint => {
    const { zoom, panX, panY } = viewRef.current;
    const u = toViewportUnits(e.clientX, e.clientY);
    return [u.x / zoom + panX, u.y / zoom + panY, e.pressure || 0.5];
  };

  // Desktop zoom/pan: ctrl+wheel and trackpad pinch (ctrlKey=true) zoom toward
  // the cursor; plain wheel pans while zoomed in. Native listener because React
  // registers wheel as passive, which blocks preventDefault.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const onWheel = (e: WheelEvent) => {
      const v = viewRef.current;
      const rect = canvas.getBoundingClientRect();
      if (e.ctrlKey) {
        e.preventDefault();
        const zoom = clamp(
          v.zoom * Math.exp(-e.deltaY * 0.005),
          MIN_ZOOM,
          MAX_ZOOM
        );
        const ux = ((e.clientX - rect.left) / rect.width) * LOGICAL_WIDTH;
        const uy = ((e.clientY - rect.top) / rect.height) * logicalHeight;
        setView(
          clampView({
            zoom,
            panX: v.panX + ux / v.zoom - ux / zoom,
            panY: v.panY + uy / v.zoom - uy / zoom,
          })
        );
      } else if (v.zoom > MIN_ZOOM) {
        e.preventDefault();
        setView(
          clampView({
            zoom: v.zoom,
            panX: v.panX + (e.deltaX / rect.width) * (LOGICAL_WIDTH / v.zoom),
            panY: v.panY + (e.deltaY / rect.height) * (logicalHeight / v.zoom),
          })
        );
      }
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [clampView, logicalHeight]);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2) {
      // Second finger landed: this is a pinch, not a stroke
      if (activePointerIdRef.current !== null) {
        activePointerIdRef.current = null;
        applyAction({ type: 'CANCEL_STROKE' });
      }
      suppressDrawingRef.current = true;
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = {
        startDist: Math.hypot(a.x - b.x, a.y - b.y),
        startMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        startView: viewRef.current,
      };
      return;
    }

    if (pointersRef.current.size === 1 && !suppressDrawingRef.current) {
      activePointerIdRef.current = e.pointerId;
      // The reducer doesn't resolve colors; capture the full style header here
      // so the relay and buffer can replay the stroke without frontend styles.
      applyAction({
        type: 'BEGIN_STROKE',
        point: toLogicalPoint(e),
        colorHex: getColorStop(state.colorFamily, state.colorStop),
        colorFamily: state.colorFamily,
        colorStop: state.colorStop,
        opacity: state.opacity,
        thickness: state.thickness,
      });
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    const pinch = pinchRef.current;
    if (pinch && pointersRef.current.size >= 2) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const zoom = clamp(
        pinch.startView.zoom * (dist / Math.max(pinch.startDist, 1)),
        MIN_ZOOM,
        MAX_ZOOM
      );
      // Keep the logical point that started under the gesture midpoint pinned
      // under the current midpoint
      const u0 = toViewportUnits(pinch.startMid.x, pinch.startMid.y);
      const u1 = toViewportUnits(mid.x, mid.y);
      setView(
        clampView({
          zoom,
          panX:
            pinch.startView.panX + u0.x / pinch.startView.zoom - u1.x / zoom,
          panY:
            pinch.startView.panY + u0.y / pinch.startView.zoom - u1.y / zoom,
        })
      );
      return;
    }

    if (activePointerIdRef.current === e.pointerId) {
      applyAction({ type: 'APPEND_POINT', point: toLogicalPoint(e) });
    }
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      pinchRef.current = null;
    }
    if (pointersRef.current.size === 0) {
      suppressDrawingRef.current = false;
    }
    if (activePointerIdRef.current === e.pointerId) {
      activePointerIdRef.current = null;
      applyAction({ type: 'END_STROKE' });
    }
  };

  const handleSubmit = async () => {
    if (!onSubmit) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Render the full drawing fresh — the live canvas may be zoomed/panned
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = EXPORT_WIDTH;
      exportCanvas.height = Math.round(EXPORT_WIDTH / aspectRatio);
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get 2D context for drawing export');
      }
      const scale = EXPORT_WIDTH / LOGICAL_WIDTH;
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      renderStrokes(ctx, state.strokes);
      await onSubmit(await exportDrawing(exportCanvas));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={toolbarFrameClassName}>
        {toolbarBefore}
        <div className="min-w-0">
          <DrawingToolbar state={state} dispatch={applyAction} />
        </div>
        {toolbarAfter}
      </div>
      <div
        className={cn(
          'flex min-h-0 w-full items-stretch justify-center',
          canvasFrameClassName
        )}
      >
        {canvasBefore}
        <div className={cn('relative min-w-0 flex-1', canvasSlotClassName)}>
          <canvas
            ref={canvasRef}
            className={cn(
              'w-full touch-none rounded-lg ring-1 ring-foreground/10 cursor-crosshair select-none',
              canvasClassName
            )}
            style={{ aspectRatio, backgroundColor: CANVAS_BACKGROUND_COLOR }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          />
          {view.zoom > MIN_ZOOM && (
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-2 right-2"
              aria-label="Reset zoom"
              onClick={() => setView(RESET_VIEW)}
            >
              {Math.round(view.zoom * 100)}% · Reset
            </Button>
          )}
        </div>
        {canvasAfter}
      </div>
      {onSubmit && (
        <Button
          size="xl"
          className="w-full"
          disabled={state.strokes.length === 0 || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? 'Submitting…' : submitLabel}
        </Button>
      )}
    </div>
  );
};
